import { OFFICIAL_NET_PORTAL_PART_UID, DEFAULT_GROUND_TERMINAL_PART_UID, DeprecatedTrackingEvents, trackingEventCopilot, } from "@buildwithflux/models";
import { omit } from "lodash";
import { applyComponentPropertiesAction } from "./applyComponentPropertiesAction";
import { applyComponentsAction } from "./applyComponentsAction";
import { applyConnectionsAction } from "./applyConnectionsAction";
import { applyDesignatorsAction } from "./applyDesignatorsAction";
import { applyNetsAction } from "./applyNetsAction";
import { applyPcbLayoutNodeRulesAction } from "./applyPcbLayoutNodeRulesAction";
import { applyProjectDescriptionAction } from "./applyProjectDescriptionAction";
import { applyProjectPropertiesAction } from "./applyProjectPropertiesAction";
/**
 * Applies action from a copilot message
 *
 * TODO in the near future, we should do a minor refactor of this so that there is a different reducer
 * action for each separate action ("rewrittenProjectDescription" etc), so that the document action
 * history will get more meaningful labels than "applied copilot actions".
 * Now that this function is a pretty thin delegator/router, that will be much easier to do and to
 * still have core/backend share most of the action code.
 */
export function applyActions({ actions, document, actionContext, componentGroups, }) {
    const actionName = Object.keys(actions)[0];
    if (!actionName || !actions[actionName]) {
        // In its current (duct-tape) form, at least, the thing that generates autopilot actions
        // can return empty. Handle this gracefully so the caller continues, on the assumption that
        // "please apply these zero-actions" is a no-op but still counts as completing the actions.
        return { appliedActions: [], appliedActionData: {}, touchedElementUids: [] };
    }
    let appliedAction;
    if (actionName === "rewriteProjectDescription") {
        appliedAction = applyProjectDescriptionAction(actions[actionName], document);
    }
    else if (actionName === "rewriteProjectProperties") {
        appliedAction = applyProjectPropertiesAction(actions[actionName], document);
    }
    else if (actionName === "rewriteDesignators") {
        appliedAction = applyDesignatorsAction(actions[actionName], document, actionContext);
    }
    else if (actionName === "rewriteComponents") {
        appliedAction = applyComponentsAction(actions[actionName], document, componentGroups, actionContext);
    }
    else if (actionName === "rewriteComponentProperties") {
        appliedAction = applyComponentPropertiesAction(actions[actionName], document, actionContext);
    }
    else if (actionName === "rewriteLayoutRules") {
        appliedAction = applyPcbLayoutNodeRulesAction(actions[actionName], document);
    }
    else if (actionName === "rewriteProjectNets") {
        appliedAction = applyNetsAction(actions[actionName], document, actionContext);
    }
    else if (actionName === "rewriteConnections") {
        appliedAction = applyConnectionsAction(actions[actionName], document, actionContext);
    }
    else {
        // Should never happen
        throw new Error(`Action '${actionName}' not valid`);
    }
    return {
        appliedActions: [actionName],
        appliedActionData: appliedAction,
        touchedElementUids: [...(appliedAction.touchedElementUids ?? [])],
    };
}
/**
 *
 */
export function trackAutopilotActions(params) {
    const { messageOwnerUid, document, segment, actions, source, threadUid, executionIntentMessageUid } = params;
    const actionName = Object.keys(actions)[0];
    if (!actionName || !actions[actionName]) {
        // In its current (duct-tape) form, at least, the thing that generates autopilot actions
        // can return empty. Handle this gracefully so the caller continues, on the assumption that
        // "please apply these zero-actions" is a no-op but still counts as completing the actions.
        return;
    }
    // When `rewriteLayoutRules`, Autopilot can be adding or removing node rules, and
    // we will send segment tracking event to track the operation
    if (actionName === "rewriteComponents") {
        const action = actions[actionName];
        for (const component of action.componentsToAdd) {
            // Legacy event for backwards compatibility
            segment.trackAutopilotAction(messageOwnerUid, DeprecatedTrackingEvents.addPartToDocument, {
                content_type: "part",
                document_uid: document.uid,
                document_name: document.name,
                document_owner: document.owner_uid,
                part_uid: component.partVersionData.part_uid,
                part_name: component.partVersionData.name,
                part_description: component.partVersionData.description,
                part_behaviour: component.partVersionData.behaviour,
                part_behaviour_is_derived: component.partVersionData.behaviour_is_derived,
                part_behaviour_has_errors: component.partVersionData.behaviour_has_errors,
                part_assets_count: Object.keys(component.partVersionData.assets || {}).length,
                part_footprint_assets_count: Object.values(component.partVersionData.assets || {}).filter((asset) => asset.isFootprint).length,
                part_3d_model_assets_count: Object.values(component.partVersionData.assets || {}).filter((asset) => asset.is3dModel).length,
                part_thumbnail_assets_count: Object.values(component.partVersionData.assets || {}).filter((asset) => asset.isThumbnail).length,
                part_owner: component.partVersionData.owner_uid,
                part_organization_owner_uid: component.partVersionData.organization_owner_uid,
            }, source, threadUid, executionIntentMessageUid);
            // New trackingEventCopilot event for analytics symmetry
            segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                surface: "agent",
                action: "add_part",
                document_uid: document.uid,
                document_name: document.name,
                document_owner: document.owner_uid,
                part_uid: component.partVersionData.part_uid,
                part_name: component.partVersionData.name,
                part_description: component.partVersionData.description,
            }, source, threadUid, executionIntentMessageUid);
        }
        for (const _component of action.componentsToRemove) {
            // Legacy event for backwards compatibility
            segment.trackAutopilotAction(messageOwnerUid, DeprecatedTrackingEvents.deleteObjects, {
                content_type: "subjects",
            }, source, threadUid, executionIntentMessageUid);
            // New trackingEventCopilot event for analytics symmetry
            segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                surface: "agent",
                action: "remove_part",
                document_uid: document.uid,
                document_name: document.name,
                document_owner: document.owner_uid,
            }, source, threadUid, executionIntentMessageUid);
        }
    }
    else if (actionName === "rewriteLayoutRules") {
        const action = actions[actionName];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        action.forEach((nodeRuleUpdate) => {
            const { nodeUid, rulesToAddOrEdit, rulesToRemove } = nodeRuleUpdate;
            if (rulesToAddOrEdit.length > 0) {
                segment.trackAutopilotAction(messageOwnerUid, DeprecatedTrackingEvents.setLayoutRule, {
                    type: "local",
                    action: "set",
                    rulesToAddOrEdit: JSON.stringify(rulesToAddOrEdit.map(({ key, value }) => ({ key, value }))),
                    nodeType: document.pcbLayoutNodes[nodeUid]?.type,
                }, source, threadUid, executionIntentMessageUid);
            }
            if (rulesToRemove.length > 0) {
                // QUESTION: Do we want to track this? We don't track this in frontend
            }
        });
    }
    else if (actionName === "rewriteProjectNets") {
        const action = actions[actionName];
        for (const netConnection of action) {
            segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                surface: "agent",
                action: "net_connection",
                document_uid: document.uid,
                document_name: document.name,
                document_owner: document.owner_uid,
                net_name: netConnection.netName,
                net_action: netConnection.action,
                pin_count: netConnection.pins.length,
            }, source, threadUid, executionIntentMessageUid);
        }
    }
    else if (actionName === "rewriteProjectProperties") {
        const action = actions[actionName];
        for (const property of action.projectPropertiesToAddOrEdit) {
            segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                surface: "agent",
                action: "project_property",
                document_uid: document.uid,
                document_name: document.name,
                document_owner: document.owner_uid,
                property_action: "add_or_edit",
                property_name: property.name,
            }, source, threadUid, executionIntentMessageUid);
        }
        for (const propertyName of action.projectPropertiesToRemove) {
            segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                surface: "agent",
                action: "project_property",
                document_uid: document.uid,
                document_name: document.name,
                document_owner: document.owner_uid,
                property_action: "remove",
                property_name: propertyName,
            }, source, threadUid, executionIntentMessageUid);
        }
    }
    else if (actionName === "rewriteDesignators") {
        const action = actions[actionName];
        for (const designator of action.componentDesignatorsToEdit) {
            segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                surface: "agent",
                action: "designator",
                document_uid: document.uid,
                document_name: document.name,
                document_owner: document.owner_uid,
                designator_type: "component",
                existing_designator: designator.existingComponentDesignator,
                new_designator: designator.newComponentDesignator,
            }, source, threadUid, executionIntentMessageUid);
        }
        for (const designator of action.netDesignatorsToEdit) {
            segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                surface: "agent",
                action: "designator",
                document_uid: document.uid,
                document_name: document.name,
                document_owner: document.owner_uid,
                designator_type: "net",
                existing_designator: designator.existingNetDesignator,
                new_designator: designator.newNetDesignator,
            }, source, threadUid, executionIntentMessageUid);
        }
    }
    else if (actionName === "rewriteComponentProperties") {
        const action = actions[actionName];
        for (const componentUpdate of action) {
            for (const property of componentUpdate.componentPropertiesToAddOrEdit) {
                segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                    surface: "agent",
                    action: "component_property",
                    document_uid: document.uid,
                    document_name: document.name,
                    document_owner: document.owner_uid,
                    component_designator: componentUpdate.componentDesignator,
                    property_action: "add_or_edit",
                    property_name: property.name,
                }, source, threadUid, executionIntentMessageUid);
            }
            for (const propertyName of componentUpdate.componentPropertiesToRemove) {
                segment.trackAutopilotAction(messageOwnerUid, trackingEventCopilot, {
                    surface: "agent",
                    action: "component_property",
                    document_uid: document.uid,
                    document_name: document.name,
                    document_owner: document.owner_uid,
                    component_designator: componentUpdate.componentDesignator,
                    property_action: "remove",
                    property_name: propertyName,
                }, source, threadUid, executionIntentMessageUid);
            }
        }
    }
}
/** Retrieve part data (and, hypothetically, any other async-or-non-pure related data)
 * that will be needed by the applyActions function to execute.
 * I don't love that we have to do this (or at least, in this hacky sorta way separate from the
 * applyActions call itself), but it's because we can't run applyActions() directly as an
 * async/nonpure function from a redux reducer.
 */
export async function enrichActionData(actions, services) {
    const portalPart = await services.partLibrary.get({ partUid: OFFICIAL_NET_PORTAL_PART_UID, partVersion: "latest" });
    const groundPart = await services.partLibrary.get({
        partUid: DEFAULT_GROUND_TERMINAL_PART_UID,
        partVersion: "latest",
    });
    if (!portalPart || !groundPart) {
        // Should never happen
        throw new Error("No portal or ground part available!");
    }
    if (!actions.rewriteComponents) {
        return {
            actions: omit(actions, "rewriteComponents"),
            actionContext: { portalPart, groundPart, logger: services.logger },
        };
    }
    const enrichedComponents = (await Promise.all(actions.rewriteComponents.componentsToAdd.map(async (component) => {
        try {
            const partDescriptor = { partUid: component.partUid, partVersion: "latest" };
            // TODO in the copilot rewriteComponents.ts code, it also handles the `partName`
            // field that the schema allows the llm to return (as a fallback if it can't
            // find the partUid). We should handle this! See also comments in
            // `schemaToCopilotActions` and `rewriteComponents.ts`
            const partVersionData = await services.partLibrary.get(partDescriptor);
            return partVersionData ? { ...component, partVersionData } : undefined;
        }
        catch (err) {
            if ("captureError" in services.logger) {
                services.logger.captureError(err);
            }
            else if ("error" in services.logger) {
                services.logger.error(err, `Error finding part ${component.partUid}`);
            }
            return undefined;
        }
    }))).filter((c) => !!c);
    return {
        actions: {
            ...actions,
            rewriteComponents: {
                ...actions.rewriteComponents,
                componentsToAdd: enrichedComponents,
            },
        },
        actionContext: { portalPart, groundPart, logger: services.logger },
    };
}
//# sourceMappingURL=applyActions.js.map